import useDocumentMeta from "../hooks/useDocumentMeta.js";
import usePageLanguage from "../hooks/usePageLanguage.js";
import { alternatesFor, canonicalUrlFor } from "../content/pages/registry.js";
import usePageShell from "../hooks/usePageShell.js";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import CookieBanner from "../components/common/CookieBanner";
import useAnalyticsContext from "../hooks/useAnalyticsContext.js";
import { trackSectionViewed, useSectionOnceObserver } from "../lib/analytics";
import PhotoSharingEditorial from "../components/wedding-photo-sharing/PhotoSharingEditorial";
import SeatingHero from "../components/wedding-seating/SeatingHero";
import SeatingFlow from "../components/wedding-seating/SeatingFlow";
import SeatingDemo from "../components/wedding-seating/SeatingDemo";
import SeatingPlan from "../components/wedding-seating/SeatingPlan";
import SeatingFaq from "../components/wedding-seating/SeatingFaq";
import SeatingFinalCta from "../components/wedding-seating/SeatingFinalCta";
import { WSE_SECTION } from "../components/wedding-seating/sections.js";

/**
 * /wedding-seating
 *
 * The fifth SEO/GEO page, and the last beat of the guest-management story:
 * /digital-wedding-invitation is what a guest opens, /wedding-rsvp is what they
 * do inside it, this is what the couple does with the answers,
 * /wedding-photo-sharing is the day itself and /wedding-planning the work
 * running underneath all of it. Same visual language on purpose — it should
 * read as another chapter, not a different site.
 *
 * ── Crawlability ──────────────────────────────────────────────────────────
 * This route also ships as a real static document (`wedding-seating.html` at
 * the project root, built as a sixth Vite entry). That file — not this
 * component — is what a crawler that does not run JavaScript receives, and it
 * carries the title, description, canonical, robots directive, social tags,
 * JSON-LD and every heading, paragraph and FAQ answer below. React replaces it
 * on mount. `weddingSeating.seo.test.mjs` asserts the two never drift apart,
 * and pins the claims the product audit ruled out.
 *
 * The meta hook still runs, because a visitor who arrives by client-side
 * navigation gets no static head.
 *
 * ── The claim boundaries that govern the whole page ───────────────────────
 * Six, and every one of them is checked in the test rather than trusted:
 *
 *  1. **Heirloom-only.** `PLAN_FEATURE_MATRIX.invitationProduct` is true for
 *     `pro` alone (api/constants/planFeatures.mjs) and names seating in the
 *     same entitlement as the invitation, RSVP and guest list; both the
 *     couple-facing seating router and the guest-facing seat lookup are mounted
 *     behind `requireProPlanWithCode`; the app hides the nav item entirely for
 *     any other plan. A Blossom or Bouquet couple has no seating at all — not a
 *     smaller one. `SeatingPlan` is what keeps the rest of the page honest,
 *     which is why it sits before the FAQ rather than after.
 *  2. **There is no drag-and-drop and no floor plan.** Nothing under
 *     app/src/pages/couple/seating/ contains a single drag/drop handler, and
 *     `shape` is a label with an icon — never geometry, never a room. The page
 *     never says "seating chart", and the FAQ answers the question plainly
 *     rather than dodging it.
 *  3. **The pool is derived, but a seat is never emptied for you.**
 *     `buildReceptionPool` (api/services/seatingPool.mjs) recomputes the
 *     seatable set on EVERY board read and is never stored — so a late
 *     acceptance appears by itself. A guest who cancels AFTER being seated
 *     keeps their chair, flagged `STALE_ASSIGNMENT` with a reason, until the
 *     couple clears it. Half of that is a lovely sentence and the other half is
 *     the honest one; the page has to carry both, which is what the "when plans
 *     change" section exists for. Nothing here may be reworded into "syncs" or
 *     "updates automatically".
 *  4. **The headcount is NOT the seating list.** `/guests/stats` counts
 *     attending members across every section and applies no `reviewStatus`
 *     filter; the pool is reception-only and approved-only. The two numbers
 *     legitimately differ, so the copy says "guests who are attending the
 *     reception appear in your seating board" and never claims parity.
 *  5. **Preferences are advisory.** `computeSeatingInsights` derives severity
 *     from `priorityLevel` and blocks nothing; the assign drawer offers "place
 *     anyway". Capacity is the one hard rule — `assertTableHasRoom` 409s before
 *     a write. The page must never blur the two into "Agapaé prevents
 *     conflicts".
 *  6. **Éros proposes; the couple applies.** `plan_seating` writes only a
 *     `SeatingPlan` draft and never a `SeatingAssignment`. It is also blocked
 *     inside the sandbox (`blockSandboxAssistant`), so the one section that
 *     offers the demo and the one that describes Éros both say so.
 *
 * ── Page shell ────────────────────────────────────────────────────────────
 * `usePageShell` — the shared bootstrap every page runs. See the hook for why
 * it re-initialises the Webflow engine from a layout effect rather than after
 * paint.
 *
 * `wps-page dwi-page` on the wrapper, not a fifth scope class: the invitation
 * page's `dwi-demo` block is exactly the framed offer this page needs for the
 * sandbox, and the photo-sharing page's `wps-flow` is exactly the ordered
 * sequence the pipeline section needs. Nothing here is new enough to earn a
 * page-specific layout prefix, and the one page-specific class that does exist
 * — the pre-render language-guard marker — lives in the static document alone.
 * A copy of it here would hide the real page from every Greek visitor.
 */
/**
 * This page's language-neutral path. The canonical and the alternates are
 * derived from it through the page registry rather than written out: the
 * canonical is self-referencing (a Greek page canonicalised to English is a
 * Greek page asking not to be indexed), and the alternates are the reciprocal
 * set the static documents declare, from the same one source.
 */
const PATH = "/wedding-seating";

const WeddingSeating = () => {
  const analyticsContext = useAnalyticsContext();

  const language = usePageLanguage();

  useDocumentMeta({
    titleKey: "meta.weddingSeating.title",
    descriptionKey: "meta.weddingSeating.description",
    canonical: canonicalUrlFor(PATH, language),
    alternates: alternatesFor(PATH),
    robots: "index, follow",
  });

  // Two impressions on the editorial run: the pipeline the positioning rests
  // on, and the trust section that is the page's real differentiator.
  // `SeatingFlow` owns its own markup but not its observer, and
  // `PhotoSharingEditorial` stays presentational — the observer rides on a
  // wrapper here rather than being pushed into a component five pages share.
  const flowRef = useSectionOnceObserver(() =>
    trackSectionViewed(WSE_SECTION.FLOW, analyticsContext),
  );
  const changesRef = useSectionOnceObserver(() =>
    trackSectionViewed(WSE_SECTION.CHANGES, analyticsContext),
  );

  usePageShell();

  return (
    <div className="page-wrapper wps-page dwi-page">
      <Navbar />
      <CookieBanner />
      <main className="main">
        <SeatingHero />

        <PhotoSharingEditorial
          headingKey="weddingSeating.overview.heading"
          bodyKeys={[
            "weddingSeating.overview.body1",
            "weddingSeating.overview.body2",
            "weddingSeating.overview.body3",
          ]}
          noteKey="weddingSeating.overview.note"
          image={{
            src: "/assets/images/moments/moment-1.jpg",
            altKey: "weddingSeating.overview.imageAlt",
            width: 6983,
            height: 3436,
          }}
        />

        <PhotoSharingEditorial
          headingKey="weddingSeating.tables.heading"
          bodyKeys={[
            "weddingSeating.tables.body1",
            "weddingSeating.tables.body2",
            "weddingSeating.tables.body3",
          ]}
          noteKey="weddingSeating.tables.note"
        />

        <PhotoSharingEditorial
          headingKey="weddingSeating.placing.heading"
          bodyKeys={[
            "weddingSeating.placing.body1",
            "weddingSeating.placing.body2",
            "weddingSeating.placing.body3",
          ]}
          noteKey="weddingSeating.placing.note"
          image={{
            src: "/assets/images/invitation/invitation-2.jpg",
            altKey: "weddingSeating.placing.imageAlt",
            width: 6558,
            height: 3659,
          }}
          imageSide="left"
        />

        <div ref={flowRef}>
          <SeatingFlow />
        </div>

        <PhotoSharingEditorial
          headingKey="weddingSeating.preferences.heading"
          bodyKeys={[
            "weddingSeating.preferences.body1",
            "weddingSeating.preferences.body2",
            "weddingSeating.preferences.body3",
          ]}
          noteKey="weddingSeating.preferences.note"
        />

        <div ref={changesRef}>
          <PhotoSharingEditorial
            headingKey="weddingSeating.changes.heading"
            bodyKeys={[
              "weddingSeating.changes.body1",
              "weddingSeating.changes.body2",
              "weddingSeating.changes.body3",
            ]}
            noteKey="weddingSeating.changes.note"
          />
        </div>

        <PhotoSharingEditorial
          headingKey="weddingSeating.export.heading"
          bodyKeys={[
            "weddingSeating.export.body1",
            "weddingSeating.export.body2",
            "weddingSeating.export.body3",
          ]}
          noteKey="weddingSeating.export.note"
          image={{
            src: "/assets/images/lovely_couple_003.jpeg",
            altKey: "weddingSeating.export.imageAlt",
            width: 1280,
            height: 714,
          }}
        />

        <PhotoSharingEditorial
          headingKey="weddingSeating.findSeat.heading"
          bodyKeys={[
            "weddingSeating.findSeat.body1",
            "weddingSeating.findSeat.body2",
            "weddingSeating.findSeat.body3",
          ]}
          noteKey="weddingSeating.findSeat.note"
          image={{
            src: "/assets/images/moments/moment_1.jpg",
            altKey: "weddingSeating.findSeat.imageAlt",
            width: 6558,
            height: 3659,
          }}
          imageSide="left"
        />

        <PhotoSharingEditorial
          headingKey="weddingSeating.eros.heading"
          bodyKeys={[
            "weddingSeating.eros.body1",
            "weddingSeating.eros.body2",
            "weddingSeating.eros.body3",
          ]}
          noteKey="weddingSeating.eros.note"
        />

        <SeatingDemo />

        <SeatingPlan />

        <SeatingFaq />

        <SeatingFinalCta />
      </main>
      <Footer />
    </div>
  );
};

export default WeddingSeating;
